<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useMapGetter } from 'dashboard/composables/store';
import { useWindowSize } from '@vueuse/core';
import { vOnClickOutside } from '@vueuse/components';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import wootConstants from 'dashboard/constants/globals';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import multiselect from 'vue-multiselect';

const { t } = useI18n();
const store = useStore();
const route = useRoute();
const { uiSettings, updateUISettings } = useUISettings();
const { width: windowWidth } = useWindowSize();

const currentAccountId = useMapGetter('getCurrentAccountId');
const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);

const isSmallScreen = computed(
  () => windowWidth.value < wootConstants.SMALL_SCREEN_BREAKPOINT
);

const IVR_ROUTES = [
  'telephony_ivr',
  'telephony_ivr_builder_new',
  'telephony_ivr_builder_edit',
  'whatsapp_calling_ivr',
  'whatsapp_calling_ivr_builder_new',
  'whatsapp_calling_ivr_builder_edit',
];

const isIvrRoute = computed(() => IVR_ROUTES.includes(route.name));

const currentPlatform = computed(() =>
  route.name?.startsWith('whatsapp_calling')
    ? 'whatsapp_calling'
    : 'conventional_voip'
);

const shouldShowPanel = computed(() => {
  const isTelephonyEnabled =
    isFeatureEnabledonAccount.value(
      currentAccountId.value,
      FEATURE_FLAGS.ASTERISK_TELEPHONY
    ) ||
    isFeatureEnabledonAccount.value(
      currentAccountId.value,
      FEATURE_FLAGS.WHATSAPP_SIP_TELEPHONY
    );
  const { is_elevenlabs_panel_open: isElevenLabsPanelOpen } = uiSettings.value;
  return isTelephonyEnabled && isElevenLabsPanelOpen && isIvrRoute.value;
});

// Close panel when navigating away from IVR routes
watch(
  () => route.name,
  () => {
    if (!isIvrRoute.value && uiSettings.value.is_elevenlabs_panel_open) {
      updateUISettings({ is_elevenlabs_panel_open: false });
    }
  }
);

const closePanel = () => {
  if (isSmallScreen.value && uiSettings.value?.is_elevenlabs_panel_open) {
    updateUISettings({ is_elevenlabs_panel_open: false });
  }
};

const closeSidebar = () => {
  updateUISettings({ is_elevenlabs_panel_open: false });
};

// Tabs for audio creation
const activeTab = ref('create');

// Form data
const audioForm = ref({
  name: '',
  description: '',
  text: '',
});

// Selected values for multiselect
const selectedVoice = ref(null);
const selectedModel = ref(null);

// Voice settings
const voiceSettings = ref({
  stability: 0.5,
  similarity_boost: 0.75,
  style: 0.0,
});

// Store getters
const voices = computed(() => store.getters['ivrAudios/getVoices']);
const models = computed(() => store.getters['ivrAudios/getModels']);
const subscription = computed(() => store.getters['ivrAudios/getSubscription']);
const uiFlags = computed(() => store.getters['ivrAudios/getUIFlags']);
const apiKeyStatus = computed(() => store.getters['ivrAudios/getApiKeyStatus']);
const isApiKeyConfigured = computed(() => apiKeyStatus.value?.configured);

// Audio preview
const previewAudioUrl = ref(null);
const previewAudioElement = ref(null);
const isPreviewPlaying = ref(false);

// Upload
const fileInput = ref(null);
const selectedFile = ref(null);

// Computed
const isFormValid = computed(() => {
  if (activeTab.value === 'create') {
    return audioForm.value.name && audioForm.value.text && selectedVoice.value;
  }
  return audioForm.value.name && selectedFile.value;
});

const characterCount = computed(() => audioForm.value.text?.length || 0);

const characterLimit = computed(() => subscription.value?.character_limit || 0);
const characterUsed = computed(() => subscription.value?.character_count || 0);
const characterRemaining = computed(
  () => characterLimit.value - characterUsed.value
);

// Methods
const resetForm = () => {
  audioForm.value = {
    name: '',
    description: '',
    text: '',
  };
  selectedFile.value = null;
  if (fileInput.value) {
    fileInput.value.value = '';
  }
  stopPreview();
};

const generateAudio = async () => {
  try {
    await store.dispatch('ivrAudios/create', {
      name: audioForm.value.name,
      description: audioForm.value.description,
      source_type: 'tts',
      tts_text: audioForm.value.text,
      tts_voice_id: selectedVoice.value.voice_id,
      tts_voice_name: selectedVoice.value.name,
      tts_model_id: selectedModel.value?.model_id || 'eleven_multilingual_v2',
      tts_voice_settings: voiceSettings.value,
      platform: currentPlatform.value,
    });

    useAlert(t('TELEPHONY.IVR.API.CREATE_SUCCESS'));
    resetForm();
    store.dispatch('ivrAudios/fetch', {
      page: 1,
      resetList: true,
      platform: currentPlatform.value,
    });
    store.dispatch('ivrAudios/fetchSubscription');
  } catch (error) {
    console.error('Error generating audio:', error);
    useAlert(t('TELEPHONY.IVR.API.ERROR'));
  }
};

const handleFileSelect = event => {
  const file = event.target.files[0];
  if (file) {
    selectedFile.value = file;
  }
};

const triggerFileInput = () => {
  fileInput.value?.click();
};

const uploadAudio = async () => {
  if (!selectedFile.value) return;

  try {
    await store.dispatch('ivrAudios/upload', {
      file: selectedFile.value,
      name: audioForm.value.name,
      description: audioForm.value.description,
      platform: currentPlatform.value,
    });

    useAlert(t('TELEPHONY.IVR.API.CREATE_SUCCESS'));
    resetForm();
    store.dispatch('ivrAudios/fetch', {
      page: 1,
      resetList: true,
      platform: currentPlatform.value,
    });
  } catch (error) {
    console.error('Error uploading audio:', error);
    useAlert(t('TELEPHONY.IVR.API.ERROR'));
  }
};

const handleSubmit = () => {
  if (activeTab.value === 'create') {
    generateAudio();
  } else {
    uploadAudio();
  }
};

// Preview voice
const playPreview = async () => {
  if (!audioForm.value.text || !selectedVoice.value) return;

  try {
    stopPreview();

    const audioUrl = await store.dispatch('ivrAudios/previewVoice', {
      voiceId: selectedVoice.value.voice_id,
      text: audioForm.value.text.substring(0, 500),
      modelId: selectedModel.value?.model_id || 'eleven_multilingual_v2',
      voiceSettings: voiceSettings.value,
    });

    previewAudioUrl.value = audioUrl;

    if (previewAudioElement.value) {
      previewAudioElement.value.src = audioUrl;
      previewAudioElement.value.play();
      isPreviewPlaying.value = true;
    }
  } catch (error) {
    console.error('Error previewing voice:', error);
  }
};

const stopPreview = () => {
  if (previewAudioElement.value) {
    previewAudioElement.value.pause();
    previewAudioElement.value.currentTime = 0;
  }
  isPreviewPlaying.value = false;
  if (previewAudioUrl.value) {
    URL.revokeObjectURL(previewAudioUrl.value);
    previewAudioUrl.value = null;
  }
};

const onPreviewEnded = () => {
  isPreviewPlaying.value = false;
};

// Load voices and subscription when panel opens
watch(shouldShowPanel, async newValue => {
  if (newValue) {
    // First check if API key is configured
    await store.dispatch('ivrAudios/fetchApiKeyStatus');

    // Only fetch voices/models/subscription if API key is configured
    if (apiKeyStatus.value?.configured) {
      await store.dispatch('ivrAudios/fetchVoices');
      await store.dispatch('ivrAudios/fetchModels');
      await store.dispatch('ivrAudios/fetchSubscription');
    }
  }
});
</script>

<template>
  <div
    v-if="shouldShowPanel"
    v-on-click-outside="() => closePanel()"
    class="bg-n-background h-full overflow-hidden flex-col fixed top-0 ltr:right-0 rtl:left-0 z-40 w-full max-w-md transition-transform duration-300 ease-in-out md:static md:w-[360px] md:min-w-[360px] ltr:border-l rtl:border-r border-n-weak shadow-lg md:shadow-none flex"
  >
    <!-- Header -->
    <div
      class="flex items-center justify-between px-4 py-3 border-b border-n-weak bg-n-solid-2"
    >
      <div class="flex items-center gap-2">
        <span class="i-lucide-audio-lines size-5 text-n-slate-11" />
        <h2 class="text-base font-semibold text-n-slate-12">ElevenLabs TTS</h2>
      </div>
      <button
        class="flex items-center justify-center size-8 rounded-md hover:bg-n-alpha-2 transition-colors"
        @click="closeSidebar"
      >
        <span class="i-lucide-x size-5 text-n-slate-11" />
      </button>
    </div>

    <!-- Content -->
    <div class="flex-1 overflow-y-auto p-4">
      <!-- API Key Not Configured Warning -->
      <div
        v-if="!isApiKeyConfigured"
        class="flex flex-col items-center justify-center py-12 text-center"
      >
        <span class="i-lucide-key-round size-12 text-n-amber-11 mb-4" />
        <h3 class="text-base font-semibold text-n-slate-12 mb-2">
          API Key não configurada
        </h3>
        <p class="text-sm text-n-slate-11 mb-4 max-w-xs">
          Configure sua ElevenLabs API Key na página de áudios para gerar áudios
          TTS.
        </p>
        <Button color="blue" size="sm" @click="closeSidebar">
          Ir para configuração
        </Button>
      </div>

      <!-- Main Content (only if API key is configured) -->
      <template v-else>
        <!-- Subscription Info -->
        <div v-if="subscription" class="mb-4 p-3 bg-n-alpha-1 rounded-lg">
          <div class="flex items-center justify-between text-sm">
            <span class="text-n-slate-11">Caracteres disponíveis:</span>
            <span class="font-medium text-n-slate-12">
              {{ characterRemaining.toLocaleString() }} /
              {{ characterLimit.toLocaleString() }}
            </span>
          </div>
          <div class="mt-2 h-1.5 bg-n-weak rounded-full overflow-hidden">
            <div
              class="h-full bg-n-brand transition-all"
              :style="{
                width: `${((characterLimit - characterRemaining) / characterLimit) * 100}%`,
              }"
            />
          </div>
        </div>

        <!-- Tabs -->
        <div class="flex border-b border-n-weak mb-4">
          <button
            class="px-4 py-2 text-sm font-medium border-b-2 transition-colors -mb-px"
            :class="[
              activeTab === 'import'
                ? 'border-n-brand-ink text-n-brand-ink'
                : 'border-transparent text-n-slate-11 hover:text-n-slate-12',
            ]"
            @click="activeTab = 'import'"
          >
            {{ t('TELEPHONY.IVR.TAB_IMPORT') }}
          </button>
          <button
            class="px-4 py-2 text-sm font-medium border-b-2 transition-colors -mb-px"
            :class="[
              activeTab === 'create'
                ? 'border-n-brand-ink text-n-brand-ink'
                : 'border-transparent text-n-slate-11 hover:text-n-slate-12',
            ]"
            @click="activeTab = 'create'"
          >
            {{ t('TELEPHONY.IVR.TAB_CREATE') }}
          </button>
        </div>

        <!-- Form Fields -->
        <div class="space-y-4">
          <!-- Name -->
          <div>
            <label class="block text-sm font-medium text-n-slate-11 mb-1">
              {{ t('TELEPHONY.IVR.FORM.NAME') }} *
            </label>
            <Input
              v-model="audioForm.name"
              :placeholder="t('TELEPHONY.IVR.FORM.NAME_PLACEHOLDER')"
            />
          </div>

          <!-- Description -->
          <div>
            <label class="block text-sm font-medium text-n-slate-11 mb-1">
              {{ t('TELEPHONY.IVR.FORM.DESCRIPTION') }}
            </label>
            <Input
              v-model="audioForm.description"
              :placeholder="t('TELEPHONY.IVR.FORM.DESCRIPTION_PLACEHOLDER')"
            />
          </div>

          <!-- Import Tab - File Upload -->
          <template v-if="activeTab === 'import'">
            <div>
              <label class="block text-sm font-medium text-n-slate-11 mb-1">
                {{ t('TELEPHONY.IVR.FORM.FILE') }} *
              </label>
              <input
                ref="fileInput"
                type="file"
                accept="audio/*"
                class="hidden"
                @change="handleFileSelect"
              />
              <div
                class="border-2 border-dashed border-n-weak rounded-lg p-4 text-center cursor-pointer hover:border-n-brand-ink transition-colors"
                @click="triggerFileInput"
              >
                <span
                  class="i-lucide-upload size-8 text-n-slate-10 mx-auto mb-2"
                />
                <p class="text-sm text-n-slate-11">
                  {{
                    selectedFile
                      ? selectedFile.name
                      : t('TELEPHONY.IVR.FORM.FILE_PLACEHOLDER')
                  }}
                </p>
              </div>
            </div>
          </template>

          <!-- Create Tab - TTS Fields -->
          <template v-else>
            <!-- Text -->
            <div>
              <label class="block text-sm font-medium text-n-slate-11 mb-1">
                {{ t('TELEPHONY.IVR.FORM.TEXT') }} *
              </label>
              <TextArea
                v-model="audioForm.text"
                :placeholder="t('TELEPHONY.IVR.FORM.TEXT_PLACEHOLDER')"
                :rows="4"
              />
              <div class="text-xs text-n-slate-10 mt-1 text-right">
                {{ characterCount }} caracteres
              </div>
            </div>

            <!-- Voice -->
            <div>
              <label class="block text-sm font-medium text-n-slate-11 mb-1">
                {{ t('TELEPHONY.IVR.FORM.VOICE') }} *
              </label>
              <div class="multiselect-wrap--small">
                <multiselect
                  v-model="selectedVoice"
                  :options="voices"
                  track-by="voice_id"
                  label="display_name"
                  :allow-empty="false"
                  :show-labels="false"
                  :loading="uiFlags.isFetchingVoices"
                  :placeholder="
                    uiFlags.isFetchingVoices
                      ? 'Carregando vozes...'
                      : 'Selecione uma voz'
                  "
                >
                  <template #option="{ option }">
                    <div class="flex flex-col py-0.5">
                      <div class="flex items-center gap-2">
                        <span class="font-medium">{{
                          option.display_name
                        }}</span>
                        <span
                          v-if="option.gender_pt"
                          class="text-[10px] px-1.5 py-0.5 rounded"
                          :class="[
                            option.gender_pt === 'Masculino'
                              ? 'bg-n-blue-3 text-n-blue-11'
                              : 'bg-n-pink-3 text-n-pink-11',
                          ]"
                        >
                          {{ option.gender_pt }}
                        </span>
                      </div>
                      <span
                        v-if="option.display_description"
                        class="text-xs text-n-slate-10"
                      >
                        {{ option.display_description }}
                      </span>
                    </div>
                  </template>
                  <template #singleLabel="{ option }">
                    <div class="flex items-center gap-2">
                      <span>{{ option.display_name }}</span>
                      <span
                        v-if="option.gender_pt"
                        class="text-[10px] px-1.5 py-0.5 rounded"
                        :class="[
                          option.gender_pt === 'Masculino'
                            ? 'bg-n-blue-3 text-n-blue-11'
                            : 'bg-n-pink-3 text-n-pink-11',
                        ]"
                      >
                        {{ option.gender_pt }}
                      </span>
                    </div>
                  </template>
                </multiselect>
              </div>
            </div>

            <!-- Voice Settings -->
            <div class="space-y-3">
              <div>
                <label class="block text-xs font-medium text-n-slate-11 mb-1">
                  Estabilidade:
                  {{ (voiceSettings.stability * 100).toFixed(0) }}%
                </label>
                <input
                  v-model.number="voiceSettings.stability"
                  type="range"
                  min="0"
                  max="1"
                  step="0.05"
                  class="w-full accent-n-brand"
                />
              </div>
              <div>
                <label class="block text-xs font-medium text-n-slate-11 mb-1">
                  Similaridade:
                  {{ (voiceSettings.similarity_boost * 100).toFixed(0) }}%
                </label>
                <input
                  v-model.number="voiceSettings.similarity_boost"
                  type="range"
                  min="0"
                  max="1"
                  step="0.05"
                  class="w-full accent-n-brand"
                />
              </div>
            </div>

            <!-- Preview -->
            <div>
              <label class="block text-sm font-medium text-n-slate-11 mb-1">
                {{ t('TELEPHONY.IVR.FORM.PREVIEW') }}
              </label>
              <audio
                ref="previewAudioElement"
                class="hidden"
                @ended="onPreviewEnded"
              />
              <div class="flex items-center gap-3 p-3 bg-n-alpha-1 rounded-lg">
                <button
                  class="flex items-center justify-center size-8 rounded-full bg-n-brand text-white hover:bg-n-brand/90 transition-colors disabled:opacity-50"
                  :disabled="
                    !audioForm.text ||
                    !selectedVoice ||
                    uiFlags.isGeneratingPreview
                  "
                  @click="isPreviewPlaying ? stopPreview() : playPreview()"
                >
                  <span
                    v-if="uiFlags.isGeneratingPreview"
                    class="i-lucide-loader-2 size-4 animate-spin"
                  />
                  <span
                    v-else
                    class="size-4"
                    :class="[
                      isPreviewPlaying ? 'i-lucide-square' : 'i-lucide-play',
                    ]"
                  />
                </button>
                <div class="flex-1 text-xs text-n-slate-11">
                  {{
                    isPreviewPlaying
                      ? 'Reproduzindo preview...'
                      : 'Clique para ouvir preview'
                  }}
                </div>
              </div>
            </div>
          </template>
        </div>
      </template>
    </div>

    <!-- Footer -->
    <div
      v-if="isApiKeyConfigured"
      class="px-4 py-3 border-t border-n-weak bg-n-solid-2"
    >
      <div class="flex gap-2">
        <Button color="slate" variant="ghost" class="flex-1" @click="resetForm">
          {{ t('TELEPHONY.COMMON.CANCEL') }}
        </Button>
        <Button
          color="blue"
          class="flex-1"
          :disabled="!isFormValid"
          :is-loading="uiFlags.isCreating || uiFlags.isUploading"
          @click="handleSubmit"
        >
          <span
            v-if="activeTab === 'create'"
            class="i-lucide-sparkles size-4 mr-1"
          />
          {{
            activeTab === 'create'
              ? t('TELEPHONY.IVR.GENERATE_AUDIO')
              : t('TELEPHONY.IVR.UPLOAD_AUDIO')
          }}
        </Button>
      </div>
    </div>
  </div>
  <template v-else />
</template>
